import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
} from "@empite/ui-components";
import type {
  ApplicationSummary,
  MarkupPlan,
  Package,
  PriceVersion,
  Reason,
} from "../admin-client";
import {
  useApplications,
  useCloneMarkupPlan,
  useClonePackage,
  useClonePriceVersion,
  useMarkupPlans,
  usePackages,
  usePriceVersions,
} from "../admin-hooks";
import { ErrorAlert, RequireOperator, can, isPlatformStaff } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { StatusBadge, when } from "./common";

/**
 * Plans — the catalogue a tenant sells from, in one place, with the platform's
 * defaults showing through it.
 *
 * WHY IT IS ONE SCREEN AND NOT THREE. Packages, markup plans and provider
 * rates are three tables, but from a tenant operator's chair they are one
 * question: what am I allowed to charge, what did I write, and what am I
 * inheriting because I never wrote anything? Pricing and Packages next door are
 * the AUTHORING consoles for those tables. This is the inheritance view, and
 * the only screen in the console where "inherited" is the organising fact.
 *
 * THE RULE THE WHOLE SCREEN IS SHAPED BY. A tenant READS the platform's
 * defaults because its routing and its retail catalogue price against them, and
 * may not EDIT one — an operator changing an inherited row would move every
 * other tenant's prices. So the only way off a default is to take a copy filed
 * under your own tenant, which is what Clone does. The server enforces this
 * twice over (scopeShared for the read, scopeColumn for the write); the split
 * lists and the Clone buttons here are the courtesy that stops an operator
 * discovering it as a 403.
 *
 * PLATFORM STAFF HAVE NO CLONE, and that is not a permission check. A clone
 * needs a tenant to land IN, and the platform is in no tenant — the API answers
 * ErrCloneNeedsTenant however senior the operator. The way platform staff
 * change a default is to edit it on Pricing or Packages, which moves the price
 * of every tenant still inheriting it.
 */
export function Plans() {
  return (
    <RequireOperator>
      {(session) => {
        const mayReadPackages = can(session.operator, "packages:read");
        const mayReadPricing = can(session.operator, "pricing:read");
        if (!mayReadPackages && !mayReadPricing) {
          return (
            <>
              <PageHeader title="Plans" />
              <Card>
                <Alert tone="info" title="This page is the billing catalogue">
                  <p>
                    Reading it needs <code>packages:read</code> or{" "}
                    <code>pricing:read</code>, and this seat holds neither.
                  </p>
                </Alert>
              </Card>
            </>
          );
        }
        return (
          <PlanCatalogue
            platform={isPlatformStaff(session.operator)}
            mayReadPackages={mayReadPackages}
            mayReadPricing={mayReadPricing}
            mayWritePackages={can(session.operator, "packages:write")}
            mayWritePricing={can(session.operator, "pricing:write")}
          />
        );
      }}
    </RequireOperator>
  );
}

/**
 * Whose row this is, in one badge. Drawn on EVERY row rather than only on the
 * inherited ones, because "no badge" is indistinguishable from "the column did
 * not render" on the screen where the distinction is the whole point.
 *
 * `inherited` is `tenant_id IS NULL` — it says the row belongs to no tenant,
 * not that it belongs to somebody else. So the opposite of it only means
 * "yours" from inside a tenant: a platform session is in none, and any owned
 * row it can see is some tenant's rather than its own.
 */
function Owner({ inherited, platform }: { inherited: boolean; platform: boolean }) {
  if (inherited) {
    return <Badge tone="warning">Platform default</Badge>;
  }
  return <Badge tone="success">{platform ? "Tenant-owned" : "Yours"}</Badge>;
}

function PlanCatalogue({
  platform,
  mayReadPackages,
  mayReadPricing,
  mayWritePackages,
  mayWritePricing,
}: {
  platform: boolean;
  mayReadPackages: boolean;
  mayReadPricing: boolean;
  mayWritePackages: boolean;
  mayWritePricing: boolean;
}) {
  const [notice, setNotice] = useState<string | null>(null);

  return (
    <>
      <PageHeader
        title="Plans"
        description="Everything your company is allowed to charge against: the packages you sell, the markup you sell them at, and the provider rates underneath. Rows marked as a platform default are read-only — clone one to get an editable copy of your own."
      />
      {platform ? (
        <Alert tone="info" title="You are looking at the defaults themselves">
          <p>
            A platform session is in no tenant, so there is nowhere for a clone
            to land and nothing below offers one. The rows marked{" "}
            <em>Platform default</em> are the ones every tenant inherits until
            it clones them; edit those on <strong>Pricing</strong> and{" "}
            <strong>Packages</strong>, remembering that a change to one moves
            the prices of every tenant still inheriting it.
          </p>
        </Alert>
      ) : null}
      {notice ? (
        <Alert tone="success" title="Cloned">
          <p>{notice}</p>
        </Alert>
      ) : null}
      {mayReadPackages ? (
        <PackageSection
          platform={platform}
          mayClone={!platform && mayWritePackages}
          onCloned={setNotice}
        />
      ) : null}
      {mayReadPricing ? (
        <MarkupPlanSection
          platform={platform}
          mayClone={!platform && mayWritePricing}
          onCloned={setNotice}
        />
      ) : null}
      {mayReadPricing ? (
        <PriceVersionSection
          platform={platform}
          mayClone={!platform && mayWritePricing}
          onCloned={setNotice}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Packages
// ---------------------------------------------------------------------------

function PackageSection({
  platform,
  mayClone,
  onCloned,
}: {
  platform: boolean;
  mayClone: boolean;
  onCloned: (message: string) => void;
}) {
  const packages = usePackages({ include_inherited: true });
  const [cloning, setCloning] = useState<Package | null>(null);

  return (
    <Card title="Packages">
      <p className="esm-muted-text">
        What a customer buys: a bundle of credit at a price, versioned by
        appending rather than by editing. A cloned default lands as a{" "}
        <strong>draft</strong> under one of your own applications, carrying the
        platform&rsquo;s current price — and carrying none of its Stripe
        identifiers, which name objects in the platform&rsquo;s own account.
      </p>
      {packages.isPending ? <Spinner label="Loading packages" /> : null}
      <ErrorAlert error={packages.error} title="Could not load the packages" />
      {cloning ? (
        <ClonePackageForm
          source={cloning}
          onClose={() => setCloning(null)}
          onDone={(message) => {
            onCloned(message);
            setCloning(null);
          }}
        />
      ) : null}
      {packages.data ? (
        <DataTable<Package>
          rows={packages.data.packages}
          rowKey={(row) => row.id}
          empty="No package is on sale, yours or the platform's."
          columns={[
            {
              key: "name",
              header: "Package",
              render: (row) => (
                <>
                  <div>{row.name}</div>
                  <code className="esm-muted-text">{row.slug}</code>
                </>
              ),
            },
            {
              key: "owner",
              header: "Source",
              render: (row) => <Owner inherited={row.inherited} platform={platform} />,
            },
            {
              key: "application",
              header: "Application",
              render: (row) => (
                <>
                  <div>{row.application_name}</div>
                  <code className="esm-muted-text">{row.application_slug}</code>
                </>
              ),
            },
            {
              key: "price",
              header: "Price",
              align: "end",
              // current_version is an explicit null rather than an absent key:
              // a package whose only version has been closed is unsellable, and
              // that is a different thing from a field that did not load.
              render: (row) =>
                row.current_version ? (
                  <>
                    <div>
                      {row.current_version.currency} {row.current_version.price}
                    </div>
                    <span className="esm-muted-text">
                      {row.current_version.granted_credit} credit
                    </span>
                  </>
                ) : (
                  <span className="esm-muted-text">no priced version</span>
                ),
            },
            {
              key: "status",
              header: "Status",
              render: (row) => <StatusBadge status={row.status} />,
            },
            {
              key: "actions",
              header: "",
              align: "end",
              render: (row) =>
                mayClone && row.inherited ? (
                  <div className="esm-actions esm-actions--table">
                    <Button variant="ghost" onClick={() => setCloning(row)}>
                      Clone
                    </Button>
                  </div>
                ) : null,
            },
          ]}
        />
      ) : null}
    </Card>
  );
}

/**
 * The package clone form.
 *
 * An application is REQUIRED, unlike the markup plan's: a package is always
 * sold under one, and the copy has to land under one of the caller's own. The
 * slug is optional and defaults to the source's, which is safe because package
 * slugs are unique per application and this copy lands under a different one.
 */
function ClonePackageForm({
  source,
  onClose,
  onDone,
}: {
  source: Package;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const applications = useApplications();
  const clone = useClonePackage();
  const [applicationID, setApplicationID] = useState("");
  const [slug, setSlug] = useState("");

  const options: ApplicationSummary[] = applications.data?.applications ?? [];

  async function run(reason: Reason) {
    const result = await clone.mutateAsync({
      packageID: source.id,
      body: {
        application_id: applicationID,
        // Sent only when the operator actually typed one, so an empty box is
        // an absence the server fills from the source rather than a slug of "".
        ...(slug.trim() === "" ? {} : { slug: slug.trim() }),
      },
      reason,
    });
    onDone(
      `${result.package.name} is now your own ${result.package.status} package under ${result.package.application_name}.`,
    );
  }

  return (
    <div className="esm-subform">
      <h3>Clone {source.name}</h3>
      <p className="esm-muted-text">
        A copy filed under your company, at the platform&rsquo;s current price.
        The default keeps working for everybody, including you, until you put
        this one on sale.
      </p>
      <ErrorAlert error={clone.error} title="The package was not cloned" />
      <ErrorAlert error={applications.error} title="Could not load your applications" />
      <div className="esm-form-grid">
        <Field
          label="Application"
          htmlFor="clone-package-application"
          hint="One of your own. A package is always sold under an application, so there is no way to clone one without naming it."
        >
          <Select
            id="clone-package-application"
            value={applicationID}
            onChange={(event) => setApplicationID(event.target.value)}
          >
            <option value="">Choose an application</option>
            {options.map((application) => (
              <option key={application.id} value={application.id}>
                {application.name} ({application.slug})
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Slug"
          htmlFor="clone-package-slug"
          hint={`Optional. Empty reuses "${source.slug}", which is free under a different application.`}
        >
          <Input
            id="clone-package-slug"
            value={slug}
            placeholder={source.slug}
            onChange={(event) => setSlug(event.target.value)}
          />
        </Field>
      </div>
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={clone.isPending}
          disabled={applicationID === ""}
          mutation={{
            title: `Clone ${source.name}`,
            tone: "primary",
            description: (
              <p>
                A draft copy under your own application, at the platform&rsquo;s
                current price. Nobody is moved onto it and nothing goes on sale
                until you publish it.
              </p>
            ),
            confirmLabel: "Clone package",
            run,
          }}
        >
          Clone package
        </ReasonButton>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Markup plans
// ---------------------------------------------------------------------------

function MarkupPlanSection({
  platform,
  mayClone,
  onCloned,
}: {
  platform: boolean;
  mayClone: boolean;
  onCloned: (message: string) => void;
}) {
  const plans = useMarkupPlans({});
  const [cloning, setCloning] = useState<MarkupPlan | null>(null);

  return (
    <Card title="Markup plans">
      <p className="esm-muted-text">
        What you add on top of what a provider charges. A plan with no
        application is the fallback every application of that owner prices
        against &mdash; which is why <em>Every application</em> and{" "}
        <em>Platform default</em> are two different columns here: one is about
        scope, the other about whose row it is.
      </p>
      {plans.isPending ? <Spinner label="Loading markup plans" /> : null}
      <ErrorAlert error={plans.error} title="Could not load the markup plans" />
      {cloning ? (
        <CloneMarkupPlanForm
          source={cloning}
          onClose={() => setCloning(null)}
          onDone={(message) => {
            onCloned(message);
            setCloning(null);
          }}
        />
      ) : null}
      {plans.data ? (
        <DataTable<MarkupPlan>
          rows={plans.data.markup_plans}
          rowKey={(row) => row.id}
          empty="No markup plan is in force, yours or the platform's."
          columns={[
            {
              key: "name",
              header: "Plan",
              render: (row) => (
                <>
                  <div>{row.name}</div>
                  <code className="esm-muted-text">{row.slug}</code>
                </>
              ),
            },
            {
              key: "owner",
              header: "Source",
              render: (row) => <Owner inherited={row.inherited} platform={platform} />,
            },
            {
              key: "scope",
              header: "Applies to",
              render: (row) =>
                row.is_platform_default ? (
                  <span className="esm-muted-text">Every application</span>
                ) : (
                  <span>{row.application_name ?? row.application_id}</span>
                ),
            },
            {
              key: "markup",
              header: "Markup",
              align: "end",
              // Basis points on the wire, percent on the screen: a margin held
              // as a float is a margin that drifts, and 1500 is not a number an
              // operator reads as 15%.
              render: (row) => (
                <>
                  <div>{(row.percentage_bps / 100).toFixed(2)}%</div>
                  <span className="esm-muted-text">
                    +{row.currency} {row.fixed_request_fee} / request
                  </span>
                </>
              ),
            },
            {
              key: "status",
              header: "Status",
              render: (row) => <StatusBadge status={row.status} />,
            },
            {
              key: "actions",
              header: "",
              align: "end",
              render: (row) =>
                mayClone && row.inherited ? (
                  <div className="esm-actions esm-actions--table">
                    <Button variant="ghost" onClick={() => setCloning(row)}>
                      Clone
                    </Button>
                  </div>
                ) : null,
            },
          ]}
        />
      ) : null}
    </Card>
  );
}

/**
 * The markup plan clone form.
 *
 * The application is OPTIONAL here and required for a package, because the two
 * tables mean different things by its absence: a package with no application
 * cannot be sold, and a markup plan with no application is the default every
 * one of your own applications falls back to. Leaving the picker empty is
 * therefore a real choice and not a missing field.
 */
function CloneMarkupPlanForm({
  source,
  onClose,
  onDone,
}: {
  source: MarkupPlan;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const applications = useApplications();
  const clone = useCloneMarkupPlan();
  const [applicationID, setApplicationID] = useState("");

  const options: ApplicationSummary[] = applications.data?.applications ?? [];

  async function run(reason: Reason) {
    const result = await clone.mutateAsync({
      planID: source.id,
      body: applicationID === "" ? {} : { application_id: applicationID },
      reason,
    });
    onDone(
      `${result.markup_plan.name} is now your own plan at ${(
        result.markup_plan.percentage_bps / 100
      ).toFixed(2)}%.`,
    );
  }

  return (
    <div className="esm-subform">
      <h3>Clone {source.name}</h3>
      <p className="esm-muted-text">
        A copy filed under your company, at the platform&rsquo;s current markup.
        Publishing it closes whatever plan of your own was in force for the same
        scope, at one instant &mdash; so a request in flight is never priced
        against two plans.
      </p>
      <ErrorAlert error={clone.error} title="The markup plan was not cloned" />
      <ErrorAlert error={applications.error} title="Could not load your applications" />
      <Field
        label="Applies to"
        htmlFor="clone-plan-application"
        hint="Leave this on 'every application of mine' for a fallback plan, or name one application to price it on its own."
      >
        <Select
          id="clone-plan-application"
          value={applicationID}
          onChange={(event) => setApplicationID(event.target.value)}
        >
          <option value="">Every application of mine</option>
          {options.map((application) => (
            <option key={application.id} value={application.id}>
              {application.name} ({application.slug})
            </option>
          ))}
        </Select>
      </Field>
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={clone.isPending}
          mutation={{
            title: `Clone ${source.name}`,
            tone: "primary",
            description: (
              <p>
                Your own copy of the platform&rsquo;s markup, in force from the
                moment it is written. It closes your previous plan for the same
                scope rather than editing it.
              </p>
            ),
            confirmLabel: "Clone markup plan",
            run,
          }}
        >
          Clone markup plan
        </ReasonButton>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Provider rates
// ---------------------------------------------------------------------------

function PriceVersionSection({
  platform,
  mayClone,
  onCloned,
}: {
  platform: boolean;
  mayClone: boolean;
  onCloned: (message: string) => void;
}) {
  const versions = usePriceVersions({});
  const clone = useClonePriceVersion();

  async function run(row: PriceVersion, reason: Reason) {
    const result = await clone.mutateAsync({ versionID: row.id, reason });
    onCloned(
      `${result.price_version.provider}/${result.price_version.model} is now priced from your own rate.`,
    );
  }

  return (
    <Card title="Provider rates">
      <p className="esm-muted-text">
        What a provider charges per million tokens, which is the floor every
        markup is applied to. There is no clone form: a rate is identified by
        the provider, model and currency it already names, and there is nothing
        about it to choose while copying.
      </p>
      {versions.isPending ? <Spinner label="Loading provider rates" /> : null}
      <ErrorAlert error={versions.error} title="Could not load the provider rates" />
      <ErrorAlert error={clone.error} title="The rate was not cloned" />
      {versions.data ? (
        <DataTable<PriceVersion>
          rows={versions.data.price_versions}
          rowKey={(row) => row.id}
          empty="No provider rate is published, yours or the platform's."
          columns={[
            {
              key: "model",
              header: "Model",
              render: (row) => (
                <>
                  <div>{row.model}</div>
                  <code className="esm-muted-text">{row.provider}</code>
                </>
              ),
            },
            {
              key: "owner",
              header: "Source",
              render: (row) => <Owner inherited={row.inherited} platform={platform} />,
            },
            {
              key: "rates",
              header: "Per million",
              align: "end",
              // Decimal strings carried verbatim from the server. A per-million
              // rate parsed into a float loses the digits that make it a rate.
              render: (row) => (
                <>
                  <div>
                    {row.currency} {row.input_per_million} in
                  </div>
                  <span className="esm-muted-text">
                    {row.output_per_million} out
                  </span>
                </>
              ),
            },
            {
              key: "from",
              header: "In force from",
              render: (row) => when(row.effective_from),
            },
            {
              key: "status",
              header: "Status",
              render: (row) => <StatusBadge status={row.status} />,
            },
            {
              key: "actions",
              header: "",
              align: "end",
              render: (row) =>
                mayClone && row.inherited ? (
                  <div className="esm-actions esm-actions--table">
                    <ReasonButton
                      busy={clone.isPending}
                      mutation={{
                        title: `Clone the rate for ${row.model}`,
                        tone: "primary",
                        description: (
                          <p>
                            Your own copy of the platform&rsquo;s{" "}
                            {row.currency} {row.input_per_million} /{" "}
                            {row.output_per_million} per million, in force from
                            the moment it is written. It closes your previous
                            rate for this provider, model and currency rather
                            than editing it — nothing already reserved is
                            re-priced.
                          </p>
                        ),
                        confirmLabel: "Clone rate",
                        run: (reason) => run(row, reason),
                      }}
                    >
                      Clone
                    </ReasonButton>
                  </div>
                ) : null,
            },
          ]}
        />
      ) : null}
    </Card>
  );
}
